import React, { useState } from "react";
import { useCustomQuery, useManualPagination } from "utilities/hookutils";
import FilterHeader from "components/filters/FilterHeader";
import { FilterProvider, type FilterProviderRef, type FilterValues } from "components/filters/FilterContext";
import { mapKeywordFilterToInstruction, mapResponseToKeywordFilter, mapGuestlistAccessTypeFilterToInstruction } from "components/filters/mappingUtils";
import KeywordFilter from "components/filters/Filters/KeywordFilter";
import Table from "components/Tables/Table";
import { useOpenGuestSidePanel } from "components/SidePanel/Guestlist/GuestSidePanel";
import GuestlistSideFilter from "components/filters/Filters/GuestlistListFilter";
import { getGuestlistListText } from "utilities/enumutils";
import { DeleteDialog } from "components";
import { useCurrentUser, useIsExternal } from "common/SessionContext";

interface GuestListProps {
  guestlistId: number
  lists: server.dto.GuestlistList[]
}

export const GuestList = (props: GuestListProps) => {
  const filterRef = React.useRef<FilterProviderRef>(null);
  const maxPerPage = 10;
  const [initialLoading, setInitialLoading] = useState(true);
  const [deletingGuestId, setDeletingGuestId] = React.useState<number | null>(null);
  const openGuestSidePanel = useOpenGuestSidePanel();
  const isExternal = useIsExternal();
  const currentUser = useCurrentUser();

  const [instruction, setInstruction] = React.useState<server.dto.PaginatedGuestInstruction>({
    guestlistId: props.guestlistId,
    guestlistAccessTypeIds: [],
    page: 1,
    perPage: maxPerPage,
  });

  const api = useCustomQuery({
    queryFn: async () => window.CoreApi.Guestlist.Paginated(instruction),
    queryKey: ["guestlist", instruction],
    onSuccess: (response) => {
      pagination.setPagination(response.data.pagination);
      filterRef.current.updateValues((values: FilterValues) => {
        return {
          keyword: mapResponseToKeywordFilter(response.data.request.keyword),
          guestlistAccessTypes: values.guestlistAccessTypes,
        };
      });

      setInitialLoading(false);
    },
  });

  const onFilterChange = (filter: FilterValues) => {
    setInstruction(mapToInstruction(filter));
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, maxPerPage, paginate);

  const mapToInstruction = (filter: FilterValues): server.dto.PaginatedGuestInstruction => {
    return {
      keyword: mapKeywordFilterToInstruction(filter.keyword),
      guestlistAccessTypeIds: mapGuestlistAccessTypeFilterToInstruction(filter.guestlistAccessTypes),
      guestlistId: props.guestlistId,
      page: 1,
      perPage: pagination.perPage,
    };
  };

  const onDelete = async () => {
    await window.CoreApi.Guestlist.DeleteGuest({ guestId: deletingGuestId });
    setDeletingGuestId(null);
    api.refetch();
  };

  const canModifyGuest = (guest: server.dto.Guest) => {
    if (!isExternal) return true;

    return guest.addedByUser.userId === currentUser.userId;
  }

  return <FilterProvider ref={filterRef} onFilterChange={onFilterChange}>
    <FilterHeader.Filters isLoading={initialLoading} compact>
      <div className="selectiontool-header-filters">
        <KeywordFilter placeholder={RESX.GeneralLabels.Search} />
        <GuestlistSideFilter initialLists={props.lists} />
      </div>
    </FilterHeader.Filters>

    {pagination.pageCount > 1 && <div className="flex justify-end padding">
      {pagination.Component}
    </div>}

    <DeleteDialog
      spinForever
      isOpen={deletingGuestId !== null}
      onDelete={onDelete}
      handleClose={() => { setDeletingGuestId(null); }}
      message={RESX.GeneralWarnings.DeleteSure}
    />

    <Table
      items={api.data?.data.data ?? []}
      getKey={guest => guest.guestId.toString()}
      isLoading={api.isLoading}
      hideHeader
      canDelete={canModifyGuest}
      onDelete={guest => setDeletingGuestId(guest.guestId)}
      columns={[{
        key: "name",
        header: "",
        render: (guest) => <div>
          {
            canModifyGuest(guest) ? <a onClick={() => openGuestSidePanel(guest, props.guestlistId)}>
              {guest.name.fullName}{guest.bringsGuests > 0 ? ` (+${guest.bringsGuests})` : ""}
            </a> : <span>
              {guest.name.fullName}{guest.bringsGuests > 0 ? ` (+${guest.bringsGuests})` : ""}
            </span>
          }
          <div className="s1_small s1_gray">{RESX.RecordInformation.CreatedBy} {guest.addedByUser.fullName}</div>
        </div>,
      }, {
        key: "accessType",
        header: "",
        render: (guest) => <>{getGuestlistListText(props.lists.find(at => at.guestlistAccessTypeId === guest.guestlistAccessTypeId))}</>,
      },
      {
        key: "owner",
        header: "",
        render: (guest) => <div>{guest.notes}</div>,
      },

      ]}
    />
  </FilterProvider>
}